/** Shared logic for Progress Report commission-received / agent-payment dates from EFTs. */

function canonicalEftType(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

export function parseEftDateToMs(raw) {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw.getTime();
  }
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(
      parseInt(iso[1], 10),
      parseInt(iso[2], 10) - 1,
      parseInt(iso[3], 10),
    );
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const d = new Date(
      parseInt(slash[3], 10),
      parseInt(slash[1], 10) - 1,
      parseInt(slash[2], 10),
    );
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function eftIssueMs(e) {
  return parseEftDateToMs(e?.chequeDate ?? e?.date);
}

/** Parse numeric EFT # from a field or reference string. */
export function parseEftNumberField(raw) {
  if (raw == null || raw === "") return null;
  const n = parseInt(String(raw).replace(/\D/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Commission received (progress report): only Real Estate Trust 1000-series
 * or standard / finalize 4000+ series issue dates — not manual tracker dates.
 */
export function isCommissionReceivedEftSeries(eftNumber) {
  const n = parseEftNumberField(eftNumber);
  if (n == null) return false;
  return (n >= 1000 && n < 2000) || n >= 4000;
}

function eftNumberFromLedgerLine(line) {
  const fromField = parseEftNumberField(line?.eftNumber);
  if (fromField != null) return fromField;
  for (const src of [line?.reference, line?.description]) {
    const m = String(src || "").match(/EFT#?\s*(\d+)/i);
    if (m) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function recordCommissionReceivedEft(map, tradeId, eft, extraCheck) {
  if (!tradeId || !eft || eft.reversed) return;
  if (!isCommissionReceivedEftSeries(eft.eftNumber)) return;
  if (extraCheck && !extraCheck(eft)) return;
  const ms = eftIssueMs(eft);
  if (ms == null) return;
  setMinMs(map, tradeId, ms);
}

function msToYmd(ms) {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function setMinMs(map, key, ms) {
  if (ms == null || !key) return;
  const prev = map.get(key);
  if (prev === undefined || ms < prev) map.set(key, ms);
}

function isCtPayoutEft(e) {
  const t = canonicalEftType(e?.type);
  return (
    t === "agentcommissiontransfer" ||
    t === "outsidebrokercommission" ||
    t === "ourbrokeragecommission" ||
    t === "builderrepayment" ||
    t === "refundofdeposit"
  );
}

/** Match trade detail / print logic for commission received on commission trust. */
export function isCommissionReceivedCtEft(e) {
  if (!e || isCtPayoutEft(e)) return false;
  const amt = parseFloat(e.amount ?? 0);
  if (!Number.isFinite(amt) || amt <= 0) return false;
  const t = canonicalEftType(e.type);
  const d = String(e.description || "").toLowerCase();
  if (t === "commissionreceived" || t === "commissiontransfer") return true;
  if (t === "generaltocommissiontransfer") {
    return (
      d.includes("commission received") ||
      d.includes("received from") ||
      d.includes("from general")
    );
  }
  if (d.includes("commission received")) return true;
  if (d.includes("received from") && !d.includes("paid to")) return true;
  return false;
}

function tradeNumberFromLedgerDescription(desc) {
  const m = String(desc || "").match(/Trade\s*#:\s*([^,]+)/i);
  return m ? String(m[1]).trim() : "";
}

function tradeIdForLedgerLine(line, tradeIdByNumber) {
  const tn = tradeNumberFromLedgerDescription(line.description);
  if (tn) {
    const id = tradeIdByNumber.get(tn.toLowerCase());
    if (id) return id;
  }
  const desc = String(line.description || "").toLowerCase();
  for (const [tnKey, tid] of tradeIdByNumber) {
    if (
      desc.includes(`trade #: ${tnKey}`) ||
      desc.includes(`trade #:${tnKey}`)
    ) {
      return tid;
    }
  }
  return null;
}

function isLedgerCommissionReceived(line) {
  const desc = String(line.description || "").toLowerCase();
  if (!desc.includes("received from") && !desc.includes("commission received")) {
    return false;
  }
  const debit = parseFloat(line.debit ?? 0);
  return Number.isFinite(debit) && debit > 0;
}

/**
 * Merge paymentReceivedEftDateIso / paymentToAgentsEftDateIso onto trade objects.
 */
export function enrichTradesWithProgressReportEftDates(
  trades,
  commissionTrustEfts = [],
  realEstateTrustEfts = [],
  ledgerLines10004 = [],
) {
  const tradeIdByNumber = new Map();
  for (const t of trades || []) {
    const tn = String(t.tradeNumber ?? t.keyInfo?.tradeNumber ?? "").trim();
    if (tn) tradeIdByNumber.set(tn.toLowerCase(), String(t._id));
  }

  const receivedMs = new Map();
  const agentPayMs = new Map();
  const outsideBrokerPayMs = new Map();
  const agentPayAmount = new Map();

  for (const e of commissionTrustEfts || []) {
    if (e.reversed) continue;
    const tid = String(e.tradeId?._id ?? e.tradeId ?? "");
    if (!tid) continue;
    const ms = eftIssueMs(e);
    if (ms == null) continue;
    const t = canonicalEftType(e.type);
    if (t === "agentcommissiontransfer") {
      setMinMs(agentPayMs, tid, ms);
      const amt = parseFloat(e.amount ?? 0);
      if (Number.isFinite(amt) && amt > 0) {
        agentPayAmount.set(tid, (agentPayAmount.get(tid) || 0) + amt);
      }
    } else if (t === "outsidebrokercommission") {
      setMinMs(outsideBrokerPayMs, tid, ms);
    } else if (isCommissionReceivedCtEft(e)) {
      recordCommissionReceivedEft(receivedMs, tid, e);
    }
  }

  for (const e of realEstateTrustEfts || []) {
    if (canonicalEftType(e.type) !== "commissiontransfer") continue;
    const tid = String(e.tradeId?._id ?? e.tradeId ?? "");
    if (!tid) continue;
    const amt = parseFloat(e.amount ?? 0);
    if (!Number.isFinite(amt) || amt <= 0) continue;
    recordCommissionReceivedEft(receivedMs, tid, e);
  }

  for (const line of ledgerLines10004 || []) {
    if (!isLedgerCommissionReceived(line)) continue;
    const tid = tradeIdForLedgerLine(line, tradeIdByNumber);
    if (!tid) continue;
    const eftNum = eftNumberFromLedgerLine(line);
    if (!isCommissionReceivedEftSeries(eftNum)) continue;
    const ms = parseEftDateToMs(line.chequeDate ?? line.date);
    if (ms == null) continue;
    setMinMs(receivedMs, tid, ms);
  }

  return (trades || []).map((trade) => {
    const tid = String(trade._id);
    const rMs = receivedMs.get(tid);
    const aMs = agentPayMs.get(tid);
    const obMs = outsideBrokerPayMs.get(tid);
    const aAmt = agentPayAmount.get(tid);
    return {
      ...trade,
      paymentReceivedEftDateIso: rMs != null ? msToYmd(rMs) : "",
      paymentToAgentsEftDateIso:
        aMs != null
          ? msToYmd(aMs)
          : String(trade.paymentToAgentsEftDateIso || "").trim(),
      paymentToOutsideBrokerEftDateIso:
        obMs != null
          ? msToYmd(obMs)
          : String(trade.paymentToOutsideBrokerEftDateIso || "").trim(),
      paymentToAgentsAmount:
        aAmt != null && aAmt > 0
          ? aAmt
          : trade.paymentToAgentsAmount ?? 0,
    };
  });
}
